import { useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function Communications({ user }) {
  const [activeTab, setActiveTab] = useState('broadcasts');
  const [broadcasts, setBroadcasts] = useState([]);
  const [notices, setNotices] = useState([]);
  const [complianceSummary, setComplianceSummary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showBroadcastModal, setShowBroadcastModal] = useState(false);
  const [showNoticeModal, setShowNoticeModal] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [broadcastForm, setBroadcastForm] = useState({
    title: '', content: '', priority: 'Routine', target_ranks: [],
    requires_acknowledgment: true, acknowledgment_deadline: '', expiry_date: '',
  });

  const [noticeForm, setNoticeForm] = useState({
    notice_type: 'Training', title: '', content: '', priority: 'Routine',
    target_ranks: [], effective_date: new Date().toISOString().split('T')[0], expiry_date: '',
  });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [broadcastsRes, noticesRes] = await Promise.all([
        axios.get(`${API}/communications/broadcasts`),
        axios.get(`${API}/communications/notices`),
      ]);
      setBroadcasts(broadcastsRes.data);
      setNotices(noticesRes.data);
      if (user.rank?.level === 5) {
        const complianceRes = await axios.get(`${API}/communications/broadcasts/compliance-summary`);
        setComplianceSummary(complianceRes.data);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load communications');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBroadcast = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const data = { ...broadcastForm };
      if (!data.acknowledgment_deadline) delete data.acknowledgment_deadline;
      if (!data.expiry_date) delete data.expiry_date;
      if (data.target_ranks.length === 0) delete data.target_ranks;
      const res = await axios.post(`${API}/communications/broadcast`, data);
      setSuccess(`Broadcast created! ${res.data.broadcast_number}`);
      setShowBroadcastModal(false);
      fetchData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create broadcast');
    }
  };

  const handleCreateNotice = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const data = { ...noticeForm };
      if (!data.expiry_date) delete data.expiry_date;
      if (data.target_ranks.length === 0) delete data.target_ranks;
      const res = await axios.post(`${API}/communications/notice`, data);
      setSuccess(`Notice created! ${res.data.notice_number}`);
      setShowNoticeModal(false);
      fetchData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create notice');
    }
  };

  const handleAcknowledge = async (broadcastId) => {
    try {
      await axios.post(`${API}/communications/broadcast/${broadcastId}/acknowledge`);
      setSuccess('Broadcast acknowledged');
      fetchData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to acknowledge');
    }
  };

  const getPriorityClass = (priority) => {
    if (priority === 'Critical') return 'bg-red-900/50 text-red-300 border border-red-700';
    if (priority === 'Urgent') return 'bg-amber-900/50 text-amber-300 border border-amber-700';
    if (priority === 'Important') return 'bg-blue-900/50 text-blue-300 border border-blue-700';
    return 'bg-slate-800 text-slate-300';
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="spinner"></div></div>;

  const unackCount = broadcasts.filter(b => !b.acknowledged && b.requires_acknowledgment).length;
  const trainingNotices = notices.filter(n => n.notice_type === 'Training');
  const deploymentNotices = notices.filter(n => n.notice_type === 'Deployment');
  const policyNotices = notices.filter(n => n.notice_type === 'Policy');

  return (
    <div className="space-y-6 animate-fade-in" data-testid="communications-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Communications & Orders</h1>
          <p className="text-slate-400">Command broadcasts, notices, and orders</p>
        </div>
        <div className="flex gap-3">
          {user.rank?.level >= 3 && (
            <button onClick={() => setShowNoticeModal(true)} className="btn-military">Issue Notice</button>
          )}
          {user.rank?.level === 5 && (
            <button onClick={() => setShowBroadcastModal(true)} className="btn-gold">Command Broadcast</button>
          )}
        </div>
      </div>

      {error && <div className="bg-red-900/30 border border-red-700 text-red-300 px-4 py-3 rounded-lg">{error}</div>}
      {success && <div className="bg-emerald-900/30 border border-emerald-700 text-emerald-300 px-4 py-3 rounded-lg">{success}</div>}

      {unackCount > 0 && (
        <div className="bg-red-900/30 border border-red-700 rounded-lg p-4">
          <p className="text-red-300 font-medium">{unackCount} Broadcast(s) Require Acknowledgment</p>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="military-card p-4"><p className="text-slate-400 text-xs">Broadcasts</p><p className="text-2xl font-bold text-white">{broadcasts.length}</p></div>
        <div className="military-card p-4"><p className="text-slate-400 text-xs">Unacknowledged</p><p className="text-2xl font-bold text-red-400">{unackCount}</p></div>
        <div className="military-card p-4"><p className="text-slate-400 text-xs">Training</p><p className="text-2xl font-bold text-blue-400">{trainingNotices.length}</p></div>
        <div className="military-card p-4"><p className="text-slate-400 text-xs">Deployment</p><p className="text-2xl font-bold text-amber-400">{deploymentNotices.length}</p></div>
        <div className="military-card p-4"><p className="text-slate-400 text-xs">Policy</p><p className="text-2xl font-bold text-purple-400">{policyNotices.length}</p></div>
      </div>

      <div className="tab-nav">
        <button onClick={() => setActiveTab('broadcasts')} className={`tab-button ${activeTab === 'broadcasts' ? 'active' : ''}`}>Broadcasts</button>
        <button onClick={() => setActiveTab('training')} className={`tab-button ${activeTab === 'training' ? 'active' : ''}`}>Training</button>
        <button onClick={() => setActiveTab('deployment')} className={`tab-button ${activeTab === 'deployment' ? 'active' : ''}`}>Deployment</button>
        <button onClick={() => setActiveTab('policy')} className={`tab-button ${activeTab === 'policy' ? 'active' : ''}`}>Policy</button>
        {user.rank?.level === 5 && <button onClick={() => setActiveTab('compliance')} className={`tab-button ${activeTab === 'compliance' ? 'active' : ''}`}>Compliance</button>}
      </div>

      {activeTab === 'broadcasts' && <BroadcastsList broadcasts={broadcasts} onAcknowledge={handleAcknowledge} getPriorityClass={getPriorityClass} />}
      {activeTab === 'training' && <NoticesList notices={trainingNotices} type="Training" getPriorityClass={getPriorityClass} />}
      {activeTab === 'deployment' && <NoticesList notices={deploymentNotices} type="Deployment" getPriorityClass={getPriorityClass} />}
      {activeTab === 'policy' && <NoticesList notices={policyNotices} type="Policy" getPriorityClass={getPriorityClass} />}
      {activeTab === 'compliance' && user.rank?.level === 5 && <ComplianceView summary={complianceSummary} />}

      {showBroadcastModal && (
        <BroadcastModal form={broadcastForm} setForm={setBroadcastForm} onSubmit={handleCreateBroadcast} onClose={() => setShowBroadcastModal(false)} />
      )}
      {showNoticeModal && (
        <NoticeModal form={noticeForm} setForm={setNoticeForm} onSubmit={handleCreateNotice} onClose={() => setShowNoticeModal(false)} />
      )}
    </div>
  );
}

function BroadcastsList({ broadcasts, onAcknowledge, getPriorityClass }) {
  if (broadcasts.length === 0) return <div className="text-center py-12 text-slate-500 military-card">No active broadcasts</div>;
  return (
    <div className="space-y-4">
      {broadcasts.map((bc) => (
        <div key={bc.id} className={`military-card p-5 ${bc.priority === 'Critical' ? 'border-l-4 border-red-500' : ''}`}>
          <div className="flex justify-between items-start mb-3">
            <div>
              <div className="flex items-center gap-3 mb-1">
                <span className="text-amber-400 font-mono text-sm">{bc.broadcast_number}</span>
                <span className={`px-2 py-1 text-xs font-semibold rounded ${getPriorityClass(bc.priority)}`}>{bc.priority}</span>
                {bc.acknowledged && <span className="px-2 py-1 text-xs rounded bg-emerald-900/50 text-emerald-300">✓ Acknowledged</span>}
              </div>
              <h3 className="text-lg font-bold text-white">{bc.title}</h3>
            </div>
            {!bc.acknowledged && bc.requires_acknowledgment && (
              <button onClick={() => onAcknowledge(bc.id)} className="btn-gold text-sm py-1 px-3">Acknowledge</button>
            )}
          </div>
          <div className="text-slate-300 text-sm whitespace-pre-wrap mb-4 p-4 bg-slate-800/50 rounded-lg">{bc.content}</div>
          <div className="flex items-center gap-4 text-xs text-slate-500">
            <span>Issued by: {bc.issued_by_name}</span>
            <span>•</span>
            <span>{new Date(bc.created_at).toLocaleString()}</span>
            {bc.acknowledgment_deadline && <><span>•</span><span>Deadline: {bc.acknowledgment_deadline}</span></>}
          </div>
        </div>
      ))}
    </div>
  );
}

function NoticesList({ notices, type, getPriorityClass }) {
  const colors = { Training: 'blue', Deployment: 'amber', Policy: 'purple' };
  const color = colors[type] || 'slate';
  if (notices.length === 0) return <div className="text-center py-12 text-slate-500 military-card">No {type.toLowerCase()} notices</div>;
  return (
    <div className="space-y-4">
      {notices.map((n) => (
        <div key={n.id} className={`military-card p-5 ${type === 'Deployment' ? 'border-l-4 border-amber-500' : ''}`}>
          <div className="flex items-center gap-3 mb-2">
            <span className={`text-${color}-400 font-mono text-sm`}>{n.notice_number}</span>
            <span className={`px-2 py-1 text-xs font-semibold rounded ${getPriorityClass(n.priority)}`}>{n.priority}</span>
          </div>
          <h3 className="text-lg font-bold text-white mb-2">{n.title}</h3>
          <p className="text-slate-300 text-sm whitespace-pre-wrap mb-3">{n.content}</p>
          <div className="flex items-center gap-4 text-xs text-slate-500">
            <span>Effective: {n.effective_date}</span>
            <span>•</span>
            <span>Issued by: {n.issued_by_name}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

function ComplianceView({ summary }) {
  if (summary.length === 0) return <div className="text-center py-12 text-slate-500 military-card">No compliance data</div>;
  return (
    <div className="military-card overflow-hidden">
      <table className="military-table">
        <thead><tr><th>Broadcast</th><th>Title</th><th>Compliance</th><th>Unacknowledged</th><th>Status</th></tr></thead>
        <tbody>
          {summary.map((item) => (
            <tr key={item.broadcast_number}>
              <td className="text-amber-400 font-mono">{item.broadcast_number}</td>
              <td className="text-white">{item.title}</td>
              <td><span className={item.compliance_rate >= 90 ? 'text-emerald-400' : item.compliance_rate >= 70 ? 'text-amber-400' : 'text-red-400'}>{item.compliance_rate}%</span></td>
              <td className={item.unacknowledged_count > 0 ? 'text-red-400' : 'text-emerald-400'}>{item.unacknowledged_count}</td>
              <td>{item.requires_escalation ? <span className="text-red-400">Escalation</span> : <span className="text-emerald-400">OK</span>}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function BroadcastModal({ form, setForm, onSubmit, onClose }) {
  return (
    <div className="modal-overlay">
      <div className="modal-content p-6 max-w-2xl">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold text-white">Command Broadcast</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white">✕</button>
        </div>
        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Title</label>
            <input type="text" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required className="input-military" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Content</label>
            <textarea value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} required className="input-military h-32 resize-none" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Priority</label>
              <select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} className="input-military">
                <option value="Routine">Routine</option>
                <option value="Important">Important</option>
                <option value="Urgent">Urgent</option>
                <option value="Critical">Critical</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Acknowledgment Deadline</label>
              <input type="date" value={form.acknowledgment_deadline} onChange={(e) => setForm({ ...form, acknowledgment_deadline: e.target.value })} className="input-military" />
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <button type="button" onClick={onClose} className="btn-military">Cancel</button>
            <button type="submit" className="btn-gold">Issue Broadcast</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function NoticeModal({ form, setForm, onSubmit, onClose }) {
  return (
    <div className="modal-overlay">
      <div className="modal-content p-6 max-w-2xl">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold text-white">Issue Notice</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white">✕</button>
        </div>
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Type</label>
              <select value={form.notice_type} onChange={(e) => setForm({ ...form, notice_type: e.target.value })} className="input-military">
                <option value="Training">Training Notice</option>
                <option value="Deployment">Deployment Order</option>
                <option value="Policy">Policy Update</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Priority</label>
              <select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} className="input-military">
                <option value="Routine">Routine</option>
                <option value="Important">Important</option>
                <option value="Urgent">Urgent</option>
                <option value="Critical">Critical</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Title</label>
            <input type="text" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required className="input-military" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Content</label>
            <textarea value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} required className="input-military h-32 resize-none" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Effective Date</label>
            <input type="date" value={form.effective_date} onChange={(e) => setForm({ ...form, effective_date: e.target.value })} required className="input-military" />
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <button type="button" onClick={onClose} className="btn-military">Cancel</button>
            <button type="submit" className="btn-gold">Issue Notice</button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default Communications;
